/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../Reducer/loaderSlice";
import Handler from "../Helpers/Handler";
import { Row, Col, Card, CardBody, Button, Badge, Spinner } from "reactstrap";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';
import dayjs from "dayjs";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';

const AnalyticsMasterDashboard = () => {
    const dispatch = useDispatch();
    const loading = useSelector(s => s.loading.loading);
    const [stats, setStats] = useState(null);

    // ⭐ MUI Filter States
    const [filters, setFilters] = useState({
        from_date: null,
        to_date: null
    });
    const [activeShortcut, setActiveShortcut] = useState('all');

    const handleShortcut = (shortcut) => {
        setActiveShortcut(shortcut);
        let from = null;
        let to = dayjs();
        
        if (shortcut === 'today') {
            from = dayjs();
            to = dayjs();
        } else if (shortcut === 'week') {
            from = dayjs().startOf('week');
        } else if (shortcut === 'month') {
            from = dayjs().startOf('month');
        } else if (shortcut === 'year') {
            from = dayjs().startOf('year');
        } else if (shortcut === 'all') {
            from = null;
            to = null;
        }
        
        setFilters({ from_date: from, to_date: to });
    };

    const loadMasterStats = async () => {
        // Validation: To date kandaipa From date-ku apram dhaan irukanum machan
        if (filters.to_date && filters.from_date && filters.to_date.isBefore(filters.from_date)) {
            alert("Machan, 'To' date must be after 'From' date!");
            return;
        }

        dispatch(loadingTrue());
        let url = `/reports/dashboard.php?1=1`;
        if (filters.from_date) url += `&from_date=${filters.from_date.format("YYYY-MM-DD")}`;
        if (filters.to_date) url += `&to_date=${filters.to_date.format("YYYY-MM-DD")}`;
        
        const res = await Handler({ method: "get", url });
        
        if (res?.success || res?.status) {
            setStats(res.data);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => { loadMasterStats(); }, [filters]);

    if (!stats) return (
        <div className="d-flex justify-content-center align-items-center min-vh-100 bg-[#f8fafc]">
            <Spinner color="primary" /> <span className="ms-3 fw-800 text-slate-600">Syncing Intelligence...</span>
        </div>
    );

    const { summary, charts } = stats;

    // Data format conversion for Recharts
    const trendData = (charts?.revenue_trend || []).map((rev) => {
        const exp = charts?.expense_trend?.find(e => e.date === rev.date);
        return {
            date: dayjs(rev.date).format("DD MMM"),
            income: Number(parseFloat(rev.total || 0).toFixed(2)),
            costs: Number(parseFloat(exp?.total || 0).toFixed(2))
        };
    });

    const expensePie = [
        { name: 'Driver Salary', value: Number(parseFloat(summary?.driver_salary || 0)) },
        { name: 'Operations', value: Number(parseFloat(summary?.other_expenses || 0)) }
    ];

    const COLORS = ['#4318FF', '#EE5D50', '#00D1FF', '#FFB547', '#10B981'];

    return (
        <div className="min-vh-100" style={{ backgroundColor: "#F4F7FE", fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            
            {/* Header Title Row */}
            <div className="mb-3">
                <h2 className="fw-800 text-dark mb-1 tracking-tight">Executive Command Center</h2>
                <p className="text-muted fw-500 mb-0">Holistic Oversight of Logistics & Finances</p>
            </div>

            {/* Filter Bar Row (Full Width & Spacious Date Pickers) */}
            <div className="mb-4 w-100">
                <div className="bg-white p-3 rounded-4 shadow-sm border d-flex align-items-center justify-content-between gap-3 flex-wrap w-100" style={{ minHeight: '56px' }}>
                    {/* Segmented Control Shortcuts */}
                    <div className="d-flex bg-light p-1 rounded-pill" style={{ backgroundColor: '#f4f7fe' }}>
                        {['all', 'today', 'week', 'month', 'year'].map((shortcut) => (
                            <Button
                                key={shortcut}
                                size="sm"
                                className="rounded-pill border-0 px-3 py-1"
                                style={{ 
                                    textTransform: 'capitalize', 
                                    fontSize: '12px', 
                                    fontWeight: '700',
                                    backgroundColor: activeShortcut === shortcut ? '#4318FF' : 'transparent',
                                    color: activeShortcut === shortcut ? '#fff' : '#a3aed0',
                                    transition: 'all 0.2s ease',
                                    boxShadow: activeShortcut === shortcut ? '0px 4px 10px rgba(67, 24, 255, 0.2)' : 'none'
                                }}
                                onClick={() => handleShortcut(shortcut)}
                            >
                                {shortcut}
                            </Button>
                        ))}
                    </div>

                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        {/* Date Pickers */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div className="d-flex align-items-center gap-2 bg-light px-3 py-1 rounded-3" style={{ border: '1px solid #e2e8f0' }}>
                                <span className="text-muted fw-bold extra-small" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>FROM</span>
                                <DatePicker
                                    value={filters.from_date}
                                    format="DD/MM/YYYY"
                                    onChange={(val) => {
                                        setFilters({ ...filters, from_date: val });
                                        setActiveShortcut('');
                                    }}
                                    slotProps={{ 
                                        textField: { 
                                            size: 'small', 
                                            variant: 'standard', 
                                            InputProps: { disableUnderline: true }, 
                                            sx: { width: 135, '& input': { fontSize: '13px', fontWeight: '700', color: '#2b3674' } } 
                                        } 
                                    }}
                                />
                            </div>
                            
                            <div className="d-flex align-items-center gap-2 bg-light px-3 py-1 rounded-3" style={{ border: '1px solid #e2e8f0' }}>
                                <span className="text-muted fw-bold extra-small" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>TO</span>
                                <DatePicker
                                    value={filters.to_date}
                                    format="DD/MM/YYYY"
                                    onChange={(val) => {
                                        setFilters({ ...filters, to_date: val });
                                        setActiveShortcut('');
                                    }}
                                    minDate={filters.from_date || undefined}
                                    slotProps={{ 
                                        textField: { 
                                            size: 'small', 
                                            variant: 'standard', 
                                            InputProps: { disableUnderline: true }, 
                                            sx: { width: 135, '& input': { fontSize: '13px', fontWeight: '700', color: '#2b3674' } } 
                                        } 
                                    }}
                                />
                            </div>
                        </LocalizationProvider>

                        {/* Premium Sleek Icon Refresh Button */}
                        <Button 
                            color="primary" 
                            className="rounded-3 d-flex align-items-center justify-content-center shadow-indigo border-0 px-3 py-2 gap-2" 
                            style={{ 
                                backgroundColor: '#4318FF',
                                fontWeight: '700',
                                fontSize: '13px'
                            }} 
                            onClick={loadMasterStats} 
                            disabled={loading}
                        >
                            {loading ? (
                                <Spinner size="sm" style={{ width: '16px', height: '16px' }} />
                            ) : (
                                <>
                                    <i className="bx bx-refresh" style={{ fontSize: '20px', color: '#fff' }}></i>
                                    <span>Refresh Data</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Row 1: KPI Cards */}
            <Row className="mb-5 g-4">
                {[
                    { label: "Cash Inflow", val: summary?.total_revenue, theme: "linear-gradient(135deg, #4318FF 0%, #5E35B1 100%)", icon: "wallet2" },
                    { label: "Net Earnings", val: summary?.net_profit, theme: "linear-gradient(135deg, #10B981 0%, #059669 100%)", icon: "graph-up-arrow" },
                    { label: "Total Fleet", val: summary?.total_vehicles ?? summary?.total_trips, theme: "linear-gradient(135deg, #FFB547 0%, #F57C00 100%)", icon: "truck" },
                    { label: "Client Growth", val: summary?.new_customers, theme: "linear-gradient(135deg, #EE5D50 0%, #C62828 100%)", icon: "people" }
                ].map((kpi, idx) => (
                    <Col key={idx} lg={3} md={6}>
                        <div className="p-4 rounded-5 shadow-lg text-white h-100 d-flex justify-content-between align-items-center transition-up" style={{ background: kpi.theme }}>
                            <div style={{ overflow: 'hidden', paddingRight: '8px' }}>
                                <span className="opacity-80 small fw-bold text-uppercase ls-1">{kpi.label}</span>
                                <h3 className="fw-800 mb-0 mt-2 text-truncate" style={{ fontSize: '1.5rem' }} title={kpi.label.includes("Cash") || kpi.label.includes("Net") ? `$${parseFloat(kpi.val || 0).toLocaleString()}` : kpi.val}>
                                    {kpi.label.includes("Cash") || kpi.label.includes("Net") ? `$${parseFloat(kpi.val || 0).toLocaleString()}` : kpi.val}
                                </h3>
                            </div>
                            <div className="d-flex align-items-center justify-content-center flex-shrink-0 ms-2" style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(255, 255, 255, 0.2)' }}>
                                <i className={`bi bi-${kpi.icon} fs-3 text-white`}></i>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* Row 2: Charts Area */}
            <Row className="g-4 mb-5">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-5 p-3">
                        <CardBody>
                            <h5 className="fw-800 text-dark mb-4">Financial Trajectory</h5>
                            <div style={{ width: '100%', height: 380 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={trendData}>
                                        <defs>
                                            <linearGradient id="colorInc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4318FF" stopOpacity={0.1}/><stop offset="95%" stopColor="#4318FF" stopOpacity={0}/></linearGradient>
                                            <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EE5D50" stopOpacity={0.1}/><stop offset="95%" stopColor="#EE5D50" stopOpacity={0}/></linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} />
                                        <Tooltip contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 15px rgba(0,0,0,0.1)' }} />
                                        <Legend verticalAlign="top" align="right" iconType="circle" />
                                        <Area name="Income" type="monotone" dataKey="income" stroke="#4318FF" strokeWidth={4} fill="url(#colorInc)" />
                                        <Area name="Costs" type="monotone" dataKey="costs" stroke="#EE5D50" strokeWidth={4} fill="url(#colorExp)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-5 p-3 h-100">
                        <CardBody>
                            <h5 className="fw-800 text-dark mb-5">Expense Allocation</h5>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={expensePie} innerRadius={80} outerRadius={110} paddingAngle={8} dataKey="value">
                                            {expensePie.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} cornerRadius={10} />)}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .rounded-5 { border-radius: 25px !important; }
                .fw-800 { font-weight: 800; }
                .transition-up:hover { transform: translateY(-8px); transition: 0.4s; cursor: pointer; }
                .shadow-indigo { box-shadow: 0px 10px 20px rgba(67, 24, 255, 0.2); }
            `}</style>
        </div>
    );
};

export default AnalyticsMasterDashboard;